import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { AlertTriangle, Truck, Wrench as WrenchIcon, MoreHorizontal, Camera, Send, X } from "lucide-react";
import CampoFirma from "@/components/CampoFirma";
import CercaMezzo from "@/components/CercaMezzo";

const TYPE_META = {
  "Near Miss": { icon: AlertTriangle, label: "Near miss" },
  "Non Conformita": { icon: Truck, label: "Non conformità" },
  "Segnalazione": { icon: WrenchIcon, label: "Segnalazione" },
  "Comunicazione": { icon: MoreHorizontal, label: "Comunicazione" },
};
const SEVERITY = [
  { v: "bassa", label: "Bassa" },
  { v: "media", label: "Media" },
  { v: "alta", label: "Alta" },
];

export default function NewReportDialog({ defaultSiteId, defaultSiteName,
                                          defaultEquipmentId, onClose, onSaved }) {
  const [types, setTypes] = useState(Object.keys(TYPE_META));
  const [sites, setSites] = useState([]);
  const [mezzi, setMezzi] = useState([]);
  const [destinatari, setDestinatari] = useState([]);
  const [equipmentId, setEquipmentId] = useState(defaultEquipmentId || "");
  const [locationType, setLocationType] = useState(defaultSiteId ? "cantiere" : "sede");
  const [siteId, setSiteId] = useState(defaultSiteId || "");
  const [newSiteName, setNewSiteName] = useState("");
  const [newSiteAddress, setNewSiteAddress] = useState("");
  const [reportType, setReportType] = useState("Near Miss");
  const [severity, setSeverity] = useState("bassa");
  const [description, setDescription] = useState("");
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const photoRef = useRef(null);
  const [firma, setFirma] = useState(null);

  useEffect(() => {
    api.get("/reports/types").then(({ data }) => setTypes(data)).catch(() => {});
    api.get("/sites").then(({ data }) => setSites(data)).catch(() => {});
    api.get("/reports/mezzi").then(({ data }) => setMezzi(data)).catch(() => {});
    api.get("/reports/destinatari")
      .then(({ data }) => setDestinatari(data.destinatari || [])).catch(() => {});
  }, []);

  const addPhotos = (e) => {
    const files = Array.from(e.target.files || []).slice(0, 5 - photos.length);
    setPhotos([...photos, ...files]);
  };
  const removePhoto = (i) => setPhotos(photos.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (!description.trim()) { toast.error("Descrivi cosa è successo"); return; }
    if (locationType === "cantiere" && !siteId) { toast.error("Seleziona un cantiere"); return; }
    if (locationType === "nuovo" && (!newSiteName.trim() || !newSiteAddress.trim())) {
      toast.error("Indica nome e indirizzo del nuovo cantiere"); return;
    }
    setBusy(true);
    const fd = new FormData();
    fd.append("location_type", locationType);
    if (locationType === "cantiere") fd.append("site_id", siteId);
    if (locationType === "nuovo") { fd.append("new_site_name", newSiteName); fd.append("new_site_address", newSiteAddress); }
    fd.append("report_type", reportType);
    fd.append("severity", severity);
    fd.append("description", description);
    if (equipmentId) fd.append("equipment_id", equipmentId);
    if (firma) fd.append("signature_base64", firma);
    photos.forEach(f => fd.append("photos", f));
    try {
      await api.post("/reports", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Segnalazione inviata");
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore nell'invio"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Nuova segnalazione</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Dove</Label>
            <div className="flex gap-2 mt-1 flex-wrap">
              {[["cantiere", "Cantiere"], ["sede", "Magazzino/Sede"], ["nuovo", "Cantiere non presente"]].map(([v, l]) => (
                <button key={v} type="button" onClick={() => setLocationType(v)}
                        className={`px-3 py-1.5 rounded-full text-xs border ${locationType === v ? "bg-orange-500 text-white border-orange-500" : "bg-white border-slate-200"}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>

          {locationType === "cantiere" && (
            <div>
              <Label>Cantiere</Label>
              <select className="w-full h-10 border border-slate-200 rounded px-3" value={siteId} onChange={(e) => setSiteId(e.target.value)}>
                <option value="">Seleziona...</option>
                {defaultSiteId && !sites.find(s => s.id === defaultSiteId) && <option value={defaultSiteId}>{defaultSiteName}</option>}
                {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          )}
          {locationType === "nuovo" && (
            <div className="grid grid-cols-1 gap-2">
              <div><Label>Nome provvisorio cantiere</Label><Input value={newSiteName} onChange={(e) => setNewSiteName(e.target.value)} /></div>
              <div><Label>Indirizzo</Label><Input value={newSiteAddress} onChange={(e) => setNewSiteAddress(e.target.value)} /></div>
              <p className="text-[10px] text-slate-500">Verrà creata una scheda cantiere minima, da completare in seguito.</p>
            </div>
          )}

          <div>
            <Label>Tipo</Label>
            <div className="grid grid-cols-2 gap-2 mt-1">
              {types.map(t => {
                const meta = TYPE_META[t] || { icon: MoreHorizontal, label: t };
                const Icon = meta.icon;
                const active = reportType === t;
                return (
                  <button key={t} type="button" onClick={() => setReportType(t)}
                          className={`flex flex-col items-center gap-1.5 p-3 rounded border text-xs ${active ? "border-orange-500 bg-orange-50 text-orange-700" : "border-slate-200"}`}>
                    <Icon className="w-5 h-5" />{meta.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Il mezzo a cui il guasto si riferisce. Si cerca invece di
              scorrere il parco: in cantiere, col telefono in mano, una
              tendina di centotrenta righe non si scorre. */}
          <div>
            <Label>Mezzo o attrezzatura (se il difetto riguarda una macchina)</Label>
            <div className="mt-1">
              <CercaMezzo mezzi={mezzi} valore={equipmentId}
                          onScegli={setEquipmentId}
                          segnaposto="Cerca targa, marca o modello — lascia vuoto se non c'entra un mezzo" />
            </div>
          </div>

          <div>
            <Label>Gravità</Label>
            <div className="flex gap-2 mt-1">
              {SEVERITY.map(s => {
                const active = severity === s.v;
                const colors = { bassa: "bg-emerald-100 text-emerald-700 border-emerald-300", media: "bg-amber-100 text-amber-700 border-amber-300", alta: "bg-red-100 text-red-700 border-red-300" };
                return (
                  <button key={s.v} type="button" onClick={() => setSeverity(s.v)}
                          className={`flex-1 py-2 rounded border text-sm ${active ? colors[s.v] : "border-slate-200 text-slate-500"}`}>
                    {s.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <Label>Descrizione</Label>
            <textarea rows={3} className="w-full border border-slate-200 rounded px-3 py-2 text-sm"
                      placeholder="Cosa è successo, dove, cosa serve..."
                      value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>

          <div>
            <input ref={photoRef} type="file" accept="image/*" multiple hidden onChange={addPhotos} />
            <Button type="button" variant="outline" className="w-full" onClick={() => photoRef.current?.click()} disabled={photos.length >= 5}>
              <Camera className="w-4 h-4 mr-2" /> Aggiungi foto ({photos.length}/5)
            </Button>
            {photos.length > 0 && (
              <div className="flex gap-2 flex-wrap mt-2">
                {photos.map((f, i) => (
                  <div key={i} className="relative w-14 h-14">
                    <img src={URL.createObjectURL(f)} alt="" className="w-14 h-14 object-cover rounded border border-slate-200" />
                    <button type="button" onClick={() => removePhoto(i)}
                            className="absolute -top-1.5 -right-1.5 bg-white border border-slate-200 rounded-full p-0.5">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <CampoFirma valore={firma} onChange={setFirma} etichetta="Firma (opzionale)" />

          {/* A chi arriva: si vede prima di premere invia, così non si resta
              col dubbio di aver scritto in un cassetto. */}
          {destinatari.length > 0 && (
            <div className="border border-slate-200 rounded-lg p-3 bg-slate-50"
                 data-testid="destinatari-segnalazione">
              <div className="flex items-center gap-2 text-xs font-medium text-slate-700">
                <Send className="w-3.5 h-3.5 text-[#0067b2]" />
                Arriva a {destinatari.length} persone
              </div>
              <p className="text-[11px] text-slate-500 mt-1">
                {destinatari.map((d) => d.name || d.email).join(", ")}.
                La trovano fra le segnalazioni, segnata come non letta.
              </p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>Annulla</Button>
          <Button type="button" onClick={submit} disabled={busy} className="bg-orange-500 hover:bg-orange-600 text-white">
            {busy ? "Invio..." : "Invia segnalazione"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
